import React from 'react';
import { 
  View, 
  Text, 
  StyleSheet, 
  ScrollView,
  Linking 
} from 'react-native';
import { useRoute, useNavigation } from '@react-navigation/native';
import { RESIDENTS_DESKTOP_COLORS } from '@/constants/residents/desktop/color';
import { PaymentModal, PaymentModalContent } from '@/components/residents/payments';
import { RESIDENTS_SVG_COMPONENTS } from '@/assets/residents';
import { RNButton } from '@/components';
import { useAppDispatch } from '@/store';
import { 
  setPayingWithSourceId,
  setPayingForResidentId,
  setIsPaymentModalOpen,
} from '@/store/slices/residents/paymentsSlice';

type Params = {
  leaseId: number;
  residentId?: number;
};

export const TransactionTimeoutScreen: React.FC = () => {
  const route = useRoute<any>();
  const navigation = useNavigation<any>();
  const dispatch = useAppDispatch();
  
  const { leaseId, residentId } = (route.params || {}) as Params;

  const resetPaymentState = () => {
    dispatch(setPayingWithSourceId(0));
    dispatch(setPayingForResidentId(0));
    dispatch(setIsPaymentModalOpen(false));
  };

  const handleClose = () => {
    resetPaymentState();
    // Navigate back to payments screen
    navigation.replace('Payments');
  };

  const handleContactSupport = () => {
    const subject = 'Payment Timeout';
    const body = `Hello Resio Support,

I experienced a payment timeout while trying to make a payment for lease ID: ${leaseId}.

Please help me check the status of my payment.

Thank you.`;
    
    const mailtoUrl = `mailto:support@resio.com?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    
    Linking.openURL(mailtoUrl).catch(() => {
      // Fallback if email app is not available
      Linking.openURL('https://resio.com/contact');
    });
  };

  return (
    <PaymentModal title="Transaction Pending" onClose={handleClose} width={520}>
      <PaymentModalContent>
        <View style={styles.header}>
          <TimeoutIcon />
        </View>
        
        <View style={styles.messageSection}>
          <Text style={styles.mainMessage}>
            Your transaction's status is unknown.
          </Text>
          
          <Text style={styles.description}>
            Our payment processing system is experiencing a delay, and it is unknown 
            whether your payment will be posted yet.
          </Text>
          
          <Text style={styles.instructions}>
            To monitor the status of your payment, please check the Transaction History 
            table under your lease. If it hasn't appeared in 15 minutes or more, please 
            contact support. We apologize for the delay.
          </Text>
        </View>
        
        <View style={styles.supportSection}>
          <SupportIcon />
          <Text style={styles.supportText}>
            Contact Resio Support
          </Text>
          <RNButton 
            title="Email Support" 
            variant="outlined" 
            onPress={handleContactSupport}
            style={styles.supportButton}
          />
          <Text style={styles.supportEmail}>
            support@resio.com
          </Text>
        </View>
        
        <View style={styles.statusSection}>
          <Text style={styles.statusTitle}>What to do next:</Text>
          
          <View style={styles.statusItem}>
            <StepIcon step="1" />
            <View style={styles.statusContent}>
              <Text style={styles.statusText}>
                Check your Transaction History in 15 minutes
              </Text>
            </View>
          </View>
          
          <View style={styles.statusItem}>
            <StepIcon step="2" />
            <View style={styles.statusContent}>
              <Text style={styles.statusText}>
                Contact support if payment doesn't appear
              </Text>
            </View>
          </View>
          
          <View style={styles.statusItem}>
            <StepIcon step="3" />
            <View style={styles.statusContent}>
              <Text style={styles.statusText}>
                Keep sufficient funds in your account
              </Text>
            </View>
          </View>
        </View>
        
        <View style={styles.footer}>
          <RNButton 
            title="Close" 
            variant="primary" 
            onPress={handleClose}
            style={styles.closeButton}
          />
        </View>
      </PaymentModalContent>
    </PaymentModal>
  );
};

// Helper Icons
const TimeoutIcon: React.FC = () => {
  const Icon = (RESIDENTS_SVG_COMPONENTS as any).CLOCK;
  return Icon ? (
    <Icon width={64} height={64} color="#F59E0B" />
  ) : (
    <View style={styles.timeoutIconFallback}>
      <Text style={styles.timeoutIconText}>⏱</Text>
    </View>
  );
};

const SupportIcon: React.FC = () => {
  const Icon = (RESIDENTS_SVG_COMPONENTS as any).CHAT_BUBBLE_LEFT_RIGHT;
  return Icon ? (
    <Icon width={24} height={24} color={RESIDENTS_DESKTOP_COLORS.BUTTON_PRIMARY} />
  ) : null;
};

const StepIcon: React.FC<{ step: string }> = ({ step }) => {
  return (
    <View style={styles.stepIcon}>
      <Text style={styles.stepIconText}>{step}</Text>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: RESIDENTS_DESKTOP_COLORS.SECONDARY_BACKGROUND,
  },
  content: {
    flex: 1,
    padding: 24,
  },
  header: {
    alignItems: 'center',
    marginBottom: 32,
  },
  timeoutIconFallback: {
    width: 64,
    height: 64,
    borderRadius: 32,
    backgroundColor: '#F59E0B',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 16,
  },
  timeoutIconText: {
    fontSize: 32,
    color: 'white',
  },
  title: {
    fontSize: 24,
    fontWeight: '700',
    color: RESIDENTS_DESKTOP_COLORS.TEXT_PRIMARY,
    marginTop: 16,
    textAlign: 'center',
  },
  messageSection: {
    marginBottom: 32,
  },
  mainMessage: {
    fontSize: 18,
    fontWeight: '600',
    color: RESIDENTS_DESKTOP_COLORS.TEXT_PRIMARY,
    textAlign: 'center',
    marginBottom: 16,
  },
  description: {
    fontSize: 16,
    color: RESIDENTS_DESKTOP_COLORS.TEXT_SECONDARY,
    textAlign: 'center',
    lineHeight: 24,
    marginBottom: 16,
  },
  instructions: {
    fontSize: 14,
    color: RESIDENTS_DESKTOP_COLORS.TEXT_TERTIARY,
    textAlign: 'center',
    lineHeight: 20,
  },
  supportSection: {
    backgroundColor: 'rgba(255,255,255,0.05)',
    borderRadius: 12,
    padding: 20,
    alignItems: 'center',
    marginBottom: 32,
  },
  supportText: {
    fontSize: 16,
    fontWeight: '600',
    color: RESIDENTS_DESKTOP_COLORS.TEXT_PRIMARY,
    marginVertical: 12,
  },
  supportButton: {
    marginVertical: 8,
    minWidth: 150,
  },
  supportEmail: {
    fontSize: 14,
    color: RESIDENTS_DESKTOP_COLORS.BUTTON_PRIMARY,
    marginTop: 8,
  },
  statusSection: {
    marginBottom: 32,
  },
  statusTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: RESIDENTS_DESKTOP_COLORS.TEXT_PRIMARY,
    marginBottom: 20,
  },
  statusItem: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: 16,
  },
  stepIcon: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: RESIDENTS_DESKTOP_COLORS.BUTTON_PRIMARY,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 16,
  },
  stepIconText: {
    fontSize: 14,
    fontWeight: '700',
    color: 'white',
  },
  statusContent: {
    flex: 1,
    paddingTop: 6,
  },
  statusText: {
    fontSize: 14,
    color: RESIDENTS_DESKTOP_COLORS.TEXT_SECONDARY,
    lineHeight: 20,
  },
  footer: {
    alignItems: 'center',
    paddingTop: 24,
  },
  closeButton: {
    minWidth: 200,
  },
});

export default TransactionTimeoutScreen;
